export function normalizeAuthReturnDestination(value, options = {}) {
	if (!options.enabled || typeof value !== 'string' || !value.startsWith('/') || value.startsWith('//')) return null;
	if (value.length > 2048 || /[\u0000-\u001f\u007f\\]/.test(value)) return null;

	let decoded;
	try {
		decoded = decodeURIComponent(value);
	} catch {
		return null;
	}
	if (decoded.startsWith('//') || /[\u0000-\u001f\u007f\\]/.test(decoded) || /\/(?:\.{1,2})(?:\/|$)/.test(decoded.split(/[?#]/, 1)[0])) return null;

	const path = value.split(/[?#]/, 1)[0];
	const loginPath = options.loginPath?.split(/[?#]/, 1)[0];
	if (loginPath && path === loginPath) return null;

	return value;
}

export function buildAuthReturnQuery(destination, options = {}) {
	const query = {
		return_to: destination,
		return_intent: options.intent || 'protected_page',
	};

	if (options.action?.type) query.return_action_type = String(options.action.type);
	if (options.action?.item_code) query.return_item_code = String(options.action.item_code);

	return query;
}

export function buildAuthReturnLocation(path, destination, options = {}) {
	return {path, query: buildAuthReturnQuery(destination, options)};
}

function selectFields(value, fields) {
	if (!value || typeof value !== 'object') return null;
	return fields.reduce((selected, field) => {
		if (value[field] !== undefined) selected[field] = value[field];
		return selected;
	}, {});
}

function canonicalize(value) {
	if (Array.isArray(value)) return value.map(canonicalize);
	if (!value || typeof value !== 'object') return value;
	return Object.keys(value).sort().reduce((result, key) => {
		result[key] = canonicalize(value[key]);
		return result;
	}, {});
}

export function createAuthCartSnapshot(cart) {
	if (!cart || typeof cart !== 'object') return null;

	const itemFields = [
		'id', 'code', 'shopping_cart_code', 'offer_shopping_cart_code', 'seller_id',
		'qty', 'quantity', 'price', 'price_custom', 'basic_price_custom', 'selected_price',
		'discount', 'discount_percent', 'discount_percent_custom', 'warranty', 'warranties',
		'service', 'services', 'selected_services', 'additional_products',
	];
	const fulfillmentFields = [
		'shipping_method', 'shipping_method_code', 'shipping_code', 'shipping_date',
		'delivery_method', 'delivery_date', 'pickup_store_id', 'store_id', 'parcel_locker_id',
		'parcel_locker_code', 'recipient_id', 'shipping_address_id', 'shipping', 'delivery',
		'pickup', 'parcel_locker', 'recipient',
	];
	const parcels = (Array.isArray(cart.parcels) ? cart.parcels : []).map(parcel => ({
		identity: selectFields(parcel, ['id', 'number', 'seller_id', 'seller_code']),
		fulfillment: selectFields(parcel, fulfillmentFields),
		items: (Array.isArray(parcel.items) ? parcel.items : [])
			.map(item => selectFields(item, itemFields))
			.sort((first, second) => JSON.stringify(first).localeCompare(JSON.stringify(second))),
	})).sort((first, second) => JSON.stringify(first).localeCompare(JSON.stringify(second)));
	const adjustments = [
		...(Array.isArray(cart.total?.extra_items) ? cart.total.extra_items : []),
		...(Array.isArray(cart.total?.extraitems) ? cart.total.extraitems : []),
	].filter(item => ['coupon', 'discount', 'promotion'].includes(item?.type))
		.map(canonicalize)
		.sort((first, second) => JSON.stringify(first).localeCompare(JSON.stringify(second)));
	const totals = selectFields(cart.total, [
		'total', 'total_basic', 'total_items', 'total_items_basic', 'total_discount',
		'total_tax', 'currency', 'currency_code',
	]);

	return JSON.stringify(canonicalize({parcels, adjustments, totals}));
}
